import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Play,
  UserRound,
  FileText,
  UsersRound,
  Users,
  BarChart3,
  Shield,
} from 'lucide-react';
import { useAudience } from '../contexts/AudienceContext';
import { trackTryDiscoveryClick } from '@/lib/gtag';
import { heroContent } from '../data/audienceContent';

const BRAND = {
  /** Primary hero CTA — same shape + gradient as Header <Button> (rounded-md, blue→teal) */
  btn: 'inline-flex h-9 w-fit shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-gradient-to-r from-blue-600 to-teal-600 px-4 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:from-blue-700 hover:to-teal-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:h-10 sm:px-5 sm:text-base',
  link: 'text-[#0066FF] underline decoration-[#0066FF] underline-offset-4 hover:text-[#0052CC]',
  /** Dark text + blue underline (student hero secondary, like design ref) */
  secondaryStudent: 'text-gray-900 underline decoration-[#0066FF] decoration-1 underline-offset-[6px] hover:text-gray-800',
  mutedLink: 'text-gray-900 underline decoration-gray-400 underline-offset-4 hover:text-[#0066FF]',
  accent: 'text-[#0066FF]',
  icon: 'text-[#0066FF]',
};

/** Soft white fades — tuned per audience so photos stay clear (school: lighter top; student: lighter bottom). */
function HeroPhotoAtmosphere({ audience }) {
  const school = audience === 'school';
  return (
    <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
      {/* Top: school uses a short, low-opacity band so faces / upper frame stay sharp */}
      <div
        className={
          school
            ? 'absolute inset-0 bg-gradient-to-b from-white from-0% via-white/14 to-transparent to-[12%]'
            : 'absolute inset-0 bg-gradient-to-b from-white from-0% via-white/42 to-transparent to-[26%]'
        }
      />
      <div className="absolute inset-0 bg-gradient-to-l from-white from-0% via-white/32 to-transparent to-[34%]" />
      <div className="absolute inset-0 hidden bg-gradient-to-r from-white from-0% via-white/16 to-transparent to-[14%] lg:block" />
      {/* Bottom: student — gentler so lower subject / desk stays clearer */}
      <div
        className={
          school
            ? 'absolute inset-0 bg-gradient-to-t from-white from-0% via-white/26 to-transparent to-[11%]'
            : 'absolute inset-0 bg-gradient-to-t from-white from-0% via-white/12 to-transparent to-[6%]'
        }
      />
      <div
        className={
          school
            ? 'absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_94%_5%,rgba(255,255,255,0.14),transparent_45%)]'
            : 'absolute inset-0 bg-[radial-gradient(ellipse_90%_58%_at_92%_6%,rgba(255,255,255,0.28),transparent_50%)]'
        }
      />
    </div>
  );
}

const ICON_MAP = {
  UserRound,
  FileText,
  UsersRound,
  Users,
  BarChart3,
  Shield,
};

function FeatureIcon({ name, className }) {
  const C = ICON_MAP[name];
  if (!C) return null;
  return <C className={className} aria-hidden />;
}

function schoolInitials(name) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const HeroSection = ({ onStartAssessment, onBookDemo, onScrollToHow }) => {
  const { audience } = useAudience();
  const copy = heroContent[audience];
  const isSchool = audience === 'school';
  const [heroImgSrc, setHeroImgSrc] = useState(copy.heroImageSrc);

  useEffect(() => {
    setHeroImgSrc(copy.heroImageSrc);
  }, [audience, copy.heroImageSrc]);

  /* Primary converts (school form / assessment start); secondary is the lower-commitment "explain it first" path. */
  const primaryAction = isSchool ? onBookDemo : onStartAssessment;

  return (
    <section
      className="relative overflow-x-clip bg-white pt-[4.75rem] pb-12 sm:pt-20 sm:pb-14 md:pt-24 md:pb-16"
      aria-labelledby="hero-heading"
    >
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        <div
          className={
            'grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-10 xl:gap-14 ' +
            (isSchool ? 'lg:min-h-[clamp(29rem,54vh,42rem)]' : '')
          }
        >
          <div
            className={
              'order-1 flex min-h-0 min-w-0 flex-col gap-5 sm:gap-6 lg:h-full lg:py-2 ' +
              (isSchool ? 'lg:justify-start' : 'lg:justify-center')
            }
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0066FF] sm:text-xs">
              {copy.eyebrow}
            </p>

            <h1
              id="hero-heading"
              className="font-display whitespace-pre-line text-[1.65rem] font-bold leading-[1.15] tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-[2.75rem]"
            >
              {copy.titleMain}
              <span className={BRAND.accent}>{copy.titleAccent}</span>
            </h1>

            <p className="max-w-xl whitespace-pre-line text-base leading-relaxed text-[#666666] sm:text-lg">
              {copy.subtitle}
            </p>

            <div className="flex flex-col items-start gap-4 pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5">
              <button type="button" onClick={primaryAction} className={BRAND.btn}>
                {copy.primaryCta}
                <ArrowRight className="size-5 shrink-0" aria-hidden />
              </button>
              <button
                type="button"
                onClick={onScrollToHow}
                className={`text-left text-base font-medium sm:text-center ${isSchool ? BRAND.mutedLink : BRAND.secondaryStudent}`}
              >
                {copy.secondaryCta}
              </button>
            </div>

            {isSchool && (
              <div
                className="mt-8 w-full max-w-xl border-t border-neutral-200/90 pt-8 lg:mt-10 lg:max-w-none lg:pt-9"
                role="list"
                aria-label="Highlights"
              >
                <div className="grid grid-cols-3 gap-0 divide-x divide-neutral-200">
                  {copy.featureBar.map((item) => (
                    <div
                      key={item.title}
                      role="listitem"
                      className="flex min-w-0 flex-col items-center px-2 py-1 text-center first:pl-0 last:pr-0 sm:px-3 lg:px-4"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-50 sm:h-14 sm:w-14">
                        <FeatureIcon name={item.icon} className={`size-5 sm:size-6 ${BRAND.icon}`} />
                      </div>
                      <h2 className="mt-3 text-[11px] font-bold leading-tight text-gray-900 sm:mt-4 sm:text-sm lg:text-base">
                        {item.title}
                      </h2>
                      <p className="mt-1.5 text-[10px] leading-snug text-[#666666] sm:text-xs">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {audience === 'student' && (
              <Link
                to="/try"
                onClick={() => trackTryDiscoveryClick('hero_card')}
                className="group relative mt-2 block max-w-xl rounded-2xl border border-gray-200 bg-gray-50/80 p-4 shadow-sm transition hover:border-[#0066FF]/30 hover:bg-white hover:shadow-md sm:p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0066FF] text-white shadow-sm">
                    <Play className="size-5 fill-white" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0066FF]">Free · No sign-up</span>
                    <span className="mt-1 block font-display text-base font-bold leading-snug text-gray-900 sm:text-lg">
                      See how Career DNA reads your style—in about a minute
                    </span>
                    <span className="mt-1 block text-sm text-[#666666]">
                      Six quick choices, no sign-up. A gentle snapshot of how you tend to think and what kinds of paths
                      might suit you.
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 self-end text-sm font-semibold text-[#0066FF] transition group-hover:gap-2 sm:self-center">
                    See my preview
                    <ArrowRight className="size-4" aria-hidden />
                  </span>
                </div>
              </Link>
            )}
          </div>

          {/* Photo + layered white atmosphere so it reads integrated, not a pasted box */}
          <div className="order-2 flex min-h-0 w-full min-w-0 flex-col lg:order-2 lg:h-full">
            <div
              className={
                'relative isolate -mx-4 h-full min-h-[min(56vw,280px)] w-[calc(100%+2rem)] overflow-hidden sm:-mx-6 sm:min-h-[min(48vw,300px)] sm:w-[calc(100%+3rem)] ' +
                'lg:mx-0 lg:min-h-full'
              }
            >
              <img
                src={heroImgSrc}
                alt={copy.heroImageAlt}
                width={1200}
                height={900}
                decoding="async"
                fetchPriority="high"
                className={
                  'relative z-0 block h-full w-full object-cover object-[center_26%] ' +
                  'min-h-[min(56vw,280px)] max-h-[68vh] contrast-[1.02] saturate-[1.03] sm:min-h-[min(48vw,300px)] sm:max-h-[72vh] ' +
                  'lg:max-h-none lg:min-h-full lg:object-[center_20%]'
                }
                onError={() => {
                  const fb = copy.heroImageFallback;
                  if (fb && heroImgSrc !== fb) setHeroImgSrc(fb);
                }}
              />
              <HeroPhotoAtmosphere audience={audience} />
            </div>
          </div>
        </div>

        {!isSchool && (
          <div
            className="mt-12 border-t border-gray-200 pt-10 sm:mt-14 sm:pt-12 lg:mt-16"
            role="list"
            aria-label="Highlights"
          >
            <div className="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              {copy.featureBar.map((item) => (
                <div
                  key={item.title}
                  role="listitem"
                  className="flex gap-4 px-0 py-6 first:pt-0 last:pb-0 sm:flex-col sm:items-start sm:px-4 sm:py-0 sm:first:pl-0 sm:last:pr-0 md:px-6"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                    <FeatureIcon name={item.icon} className={`size-6 ${BRAND.icon}`} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-gray-900">{item.title}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#666666]">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isSchool && copy.heroTrust && (
          <div className="mt-10 border-t border-gray-200 pt-10 sm:mt-12 sm:pt-12">
            <p className="text-center text-xs font-medium uppercase tracking-wider text-[#666666] sm:text-sm">
              {copy.heroTrust.eyebrow}
            </p>
            <div className="mt-6 flex gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:grid sm:grid-cols-5 sm:gap-4 sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden">
              {copy.heroTrust.schools.map((s) => (
                <div
                  key={`${s.name}-${s.city}`}
                  className="flex min-w-[11.5rem] shrink-0 snap-start items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-3 shadow-sm sm:min-w-0"
                >
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold uppercase text-gray-600"
                    aria-hidden
                  >
                    {schoolInitials(s.name)}
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="text-xs font-bold leading-snug text-gray-900">{s.name}</div>
                    <div className="text-[10px] text-[#666666]">{s.city}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
